import React, { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faFolderOpen, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import AsyncCreatableSelect from "react-select/async-creatable";
import { Modal } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import Input from "../../../nodes/Logic/Inputs/Input";
import PhoneInputComponent from "../../../nodes/Logic/Inputs/PhoneInputComponent";
import MapInput from "../../../nodes/Logic/Inputs/MapInput";
import {
  getAssetValueLabels,
  listWorkflowFileTags,
  listWorkflowFiles,
  loadAssetOptionsPaginated,
  loadContactOptionsPaginated,
  loadEventOptionsPaginated,
} from "../../../../proxyAPI";
import MyPagination from "../../../../Common/MyPagination";
import { contactOption } from "../../../../Reporting/RealtimeReportingPage";
import { pageProps } from "../../../../helpers/pageProps";
import timezones from "../../../nodes/Logic/timezones";
import TokenEditor from "../../TokenEditor";
import { InputArgument } from "./types";
import { formatDateValue } from "./dateUtils";
import { getFieldError } from "./fieldValidation";
import { getButtonInputOptions } from "../../inputArgumentOptionHelpers";
import AssetsIcon from "../../../../Assets/AssetsIcon";

const FILE_TYPE_EXTENSIONS: Record<string, string[]> = {
  json: ["json"],
  csv: ["csv"],
  pdf: ["pdf"],
  excel: ["xls", "xlsx"],
  txt: ["txt"],
  doc: ["doc", "docx"],
};

const formatBytes = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
};

const getFileExtension = (fileName: string) =>
  fileName.split(".").pop()?.toLowerCase() || "";

const getEncodedFileName = (value: string) =>
  value.match(/^filename:([^|]+)/)?.[1] || value.split("/").pop() || value;

const normaliseFilesResponse = (data: any) => data?.files || data?.Files || data || [];
const normaliseFilesTotal = (data: any) => Number(data?.total ?? data?.Total ?? normaliseFilesResponse(data).length);
const fileUuid = (file: any) => file.UUID || file.Uuid || file.uuid;
const fileName = (file: any) => file.Filename || file.filename || file.FileName || file.file_name || fileUuid(file);
const fileReference = (file: any) => file.Reference || file.reference || (file.ObjectKey || file.object_key ? `file:${file.ObjectKey || file.object_key}` : "");
const fileSize = (file: any) => file.SizeBytes ?? file.size_bytes ?? file.sizeBytes ?? file.Size ?? file.size ?? null;
const fileDirectories = (file: any) => file.Directories || file.directories || [];
const fileCreatedAt = (file: any) => file.CreatedAt || file.created_at || file.createdAt || "";

const directoryOptions = (directories: string[]) =>
  directories.map((directory) => ({ value: directory, label: directory }));

const optionsToDirectories = (options: any) =>
  (options || []).map((option: any) => option.value).filter(Boolean);

const formatFileSize = (bytes: unknown) => {
  if (bytes === null || bytes === undefined || bytes === "") return "";
  const numericBytes = Number(bytes);
  if (!Number.isFinite(numericBytes)) return "";
  if (numericBytes < 1024) return `${numericBytes} B`;
  if (numericBytes < 1024 * 1024) return `${Math.round(numericBytes / 1024)} KB`;
  return `${Math.round((numericBytes / (1024 * 1024)) * 10) / 10} MB`;
};

const formatUploadedAt = (value: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const normaliseFileSource = (source?: string) => {
  const value = (source || "direct").toLowerCase();
  if (["direct", "library"].includes(value)) return value;
  return "direct";
};

interface InputArgumentFieldProps {
  arg: InputArgument;
  /** The current values for this argument (i.e. formData[arg.Field]) */
  values: string[];
  /** Called with the complete new values array whenever the field changes */
  onChange: (newValues: string[]) => void;
  /** Pre-filtered list of input arguments shown inside the token editor */
  inputArgumentsInEditor: InputArgument[];
  /** Render contact/group selectors as offcanvas when true */
  useOffcanvasForContactGroupModal?: boolean;
  projectUuid?: string;
}

const formatAssetOptionLabel = (option: any) => {
  const asset = option.data;
  const assetTypeLabel = option.assetTypeLabel;

  if (!asset || !assetTypeLabel) {
    return option.label;
  }

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span>{asset.Label}</span>
      <span className="badge bg-light text-dark border d-inline-flex align-items-center gap-1">
        <AssetsIcon assetKind={asset.Kind} width={12} />
        {assetTypeLabel}
      </span>
      <span className="text-muted small">
        {asset.Stats?.Locations || 0} locations
      </span>
    </div>
  );
};

const VariableTextInput = ({
  arg,
  values,
  onChange,
  placeholder,
}: {
  arg: InputArgument;
  values: string[];
  onChange: (newValues: string[]) => void;
  placeholder: string;
}) => (
  <>
    {arg.IsMultiple && (
      <button
        type="button"
        title="Add input"
        className="btn btn-outline-primary btn-sm border-0 float-end"
        onClick={() => onChange([...(values ?? []), ""])}
      >
        <FontAwesomeIcon icon={faPlus} /> Add
      </button>
    )}
    {(values.length > 0 ? values : [""]).map((value, index) => (
      <div key={index} className="d-flex mb-1">
        <div className="flex-grow-1">
          <textarea
            placeholder={placeholder}
            className="form-control mt-1"
            value={value}
            onChange={(e) => {
              if (!values || values.length === 0) {
                onChange([e.target.value]);
                return;
              }
              onChange(
                values.map((item, i) => (i === index ? e.target.value : item))
              );
            }}
          />
        </div>
        {arg.IsMultiple && values.length > 1 && (
          <button
            type="button"
            title="Remove input"
            className="btn btn-outline-danger border-0 float-end"
            onClick={() => onChange(values.filter((_, i) => i !== index))}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        )}
      </div>
    ))}
  </>
);

const VariableDurationInput = ({
  arg,
  values,
  onChange,
  placeholder,
}: {
  arg: InputArgument;
  values: string[];
  onChange: (newValues: string[]) => void;
  placeholder: string;
}) => (
  <>
    {arg.IsMultiple && (
      <button
        type="button"
        title="Add input"
        className="btn btn-outline-primary btn-sm border-0 float-end"
        onClick={() => onChange([...(values ?? []), "0|second"])}
      >
        <FontAwesomeIcon icon={faPlus} /> Add
      </button>
    )}
    {(values.length > 0 ? values : [""]).map((value, index) => (
      <div key={index} className="d-flex mb-1">
        <div className="flex-grow-1">
          <Input
            placeholder={placeholder}
            inputHelp=""
            value={[value.split("|")[0] || ""]}
            onChange={([v]) =>
              onChange(
                values.map((item, i) =>
                  i === index ? v + "|" + (item.split("|")[1] || "") : item
                )
              )
            }
          />
        </div>
        <div className="flex-grow-1">
          <Input
            placeholder={placeholder}
            inputType="select"
            options={
              arg.Unit?.map((unit) => ({ value: unit, label: unit })) || []
            }
            isValidNewOption={() => false}
            inputHelp=""
            value={[value.split("|")[1] || ""]}
            onChange={([v]) =>
              onChange(
                values.map((item, i) =>
                  i === index ? (item.split("|")[0] || "") + "|" + v : item
                )
              )
            }
          />
        </div>
        {arg.IsMultiple && values.length > 1 && (
          <button
            type="button"
            title="Remove input"
            className="btn btn-outline-danger border-0 float-end"
            onClick={() => onChange(values.filter((_, i) => i !== index))}
          >
            <FontAwesomeIcon icon={faMinus} />
          </button>
        )}
      </div>
    ))}
  </>
);

const BinaryFileInput = ({
  arg,
  values,
  onChange,
  projectUuid,
}: {
  arg: InputArgument;
  values: string[];
  onChange: (newValues: string[]) => void;
  projectUuid?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState<any[]>([]);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryTags, setLibraryTags] = useState<string[]>([]);
  const allowedTypes = arg.FileAllowedTypes || [];
  const allowedExtensions = allowedTypes.flatMap(
    (type) => FILE_TYPE_EXTENSIONS[type] || []
  );
  const accept = allowedExtensions.map((ext) => `.${ext}`).join(",");
  const fileSource = normaliseFileSource(arg.FileSource);
  const canUploadDirectly = fileSource === "direct";
  const canSelectFromLibrary = fileSource === "library";
  const sourceColumnClass = canUploadDirectly && canSelectFromLibrary ? "col-12 col-md-6" : "col-12";
  const libraryFilterKey = useMemo(
    () => JSON.stringify({ projectUuid, librarySearch, libraryTags, allowedExtensions }),
    [projectUuid, librarySearch, libraryTags, allowedExtensions]
  );

  const loadTagOptions = async (inputValue: string) => {
    const data = await listWorkflowFileTags(inputValue);
    return (data?.tags || data?.Tags || []).map((tag: string) => ({ value: tag, label: tag }));
  };

  const loadLibraryFiles = async (page: number, perPage: number) => {
    setLibraryLoading(true);
    try {
      const data = await listWorkflowFiles({
        projectUuid,
        search: librarySearch,
        tags: libraryTags,
        extensions: allowedExtensions,
        scope: "permanent",
        limit: perPage,
        offset: page * perPage,
      });
      setLibraryFiles(normaliseFilesResponse(data));
      setLibraryTotal(normaliseFilesTotal(data));
    } finally {
      setLibraryLoading(false);
    }
  };

  const validateFile = (file: File) => {
    if (arg.FileMaxBytes && file.size > arg.FileMaxBytes) {
      return `${file.name} is larger than ${formatBytes(arg.FileMaxBytes)}.`;
    }

    if (
      allowedExtensions.length &&
      !allowedExtensions.includes(getFileExtension(file.name))
    ) {
      return `${file.name} is not an allowed file type.`;
    }

    return "";
  };

  const encodeFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) {
      onChange([]);
      return;
    }

    const selectedFiles = arg.IsMultiple ? files : files.slice(0, 1);
    const errors = selectedFiles.map(validateFile).filter(Boolean);
    if (errors.length) {
      toast.error(errors[0], { toastId: "binary-file-validation" });
      return;
    }

    try {
      const encoded = await Promise.all(
        selectedFiles.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onerror = reject;
              reader.onload = () =>
                resolve(
                  `filename:${file.name}|${(reader.result as string).replace(
                    ";base64,",
                    "|base64:"
                  )}`
                );
            })
        )
      );
      onChange(encoded);
    } catch (err) {
      console.error(err);
      toast.error("Unable to read the selected file.");
    }
  };

  return (
    <div>
      <div className="row g-2">
        {canUploadDirectly && (
        <div className={sourceColumnClass}>
          <button
            type="button"
            className={`btn w-100 border d-flex flex-column align-items-center justify-content-center gap-2 py-3 h-100 ${
              isDragging ? "btn-primary" : "btn-light"
            }`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              encodeFiles(event.dataTransfer.files);
            }}
          >
            <FontAwesomeIcon icon={faFileArrowUp} size="lg" />
            <span>{values.length ? "Upload replacement" : "Upload new file"}</span>
            <span className="small text-muted">
              {allowedTypes.length ? allowedTypes.join(", ").toUpperCase() : "Any file"}
              {arg.FileMaxBytes ? ` up to ${formatBytes(arg.FileMaxBytes)}` : ""}
            </span>
          </button>
        </div>
        )}
        {canSelectFromLibrary && (
        <div className={sourceColumnClass}>
          <button
            type="button"
            className="btn btn-light w-100 border d-flex flex-column align-items-center justify-content-center gap-2 py-3 h-100"
            onClick={() => setShowLibrary(true)}
          >
            <FontAwesomeIcon icon={faFolderOpen} size="lg" />
            <span>{values.length ? "Select replacement" : "Select from library"}</span>
            <span className="small text-muted">Permanent files only</span>
          </button>
        </div>
        )}
      </div>
      {canUploadDirectly && (
        <input
          ref={inputRef}
          type="file"
          className="d-none"
          multiple={arg.IsMultiple}
          accept={accept || undefined}
          onChange={(event) => {
            if (!event.target.files?.length) {
              onChange([]);
              return;
            }
            encodeFiles(event.target.files);
            event.target.value = "";
          }}
        />
      )}
      {values.length > 0 && (
        <ul className="list-group list-group-flush mt-2">
          {values.map((value, index) => (
            <li
              key={`${getEncodedFileName(value)}-${index}`}
              className="list-group-item px-0 py-1 d-flex align-items-center justify-content-between"
            >
              <span className="text-truncate small">{getEncodedFileName(value)}</span>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm border-0"
                title="Remove file"
                onClick={() => onChange(values.filter((_, i) => i !== index))}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {canSelectFromLibrary && (
      <Modal show={showLibrary} onHide={() => setShowLibrary(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Select file from library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ minHeight: 620 }}>
            <div className="bg-colored-reverse p-3 rounded-3 mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-6">
                  <label className="form-label">Search</label>
                  <input
                    type="search"
                    className="form-control"
                    value={librarySearch}
                    onChange={(event) => setLibrarySearch(event.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Tags</label>
                  <AsyncCreatableSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    loadOptions={loadTagOptions}
                    value={directoryOptions(libraryTags)}
                    onChange={(options) => setLibraryTags(optionsToDirectories(options))}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Filter by tag..."
                    noOptionsMessage={() => "No tags found"}
                  />
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Size</th>
                    <th>Tags</th>
                    <th>Uploaded</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {libraryFiles.length > 0 ? (
                    libraryFiles.map((file) => (
                      <tr key={fileUuid(file)}>
                        <td>{fileName(file)}</td>
                        <td>{formatFileSize(fileSize(file)) || <span className="text-muted">-</span>}</td>
                        <td>
                          {fileDirectories(file).length > 0 ? (
                            fileDirectories(file).join(", ")
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{formatUploadedAt(fileCreatedAt(file)) || <span className="text-muted">-</span>}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-colored"
                            onClick={() => {
                              const reference = fileReference(file);
                              if (!reference) {
                                toast.error("Unable to select this file.");
                                return;
                              }
                              onChange([reference]);
                              setShowLibrary(false);
                            }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-5">
                        {libraryLoading ? "Loading files" : "No files"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <MyPagination
              key={libraryFilterKey}
              total={libraryTotal}
              defaultPerPage={10}
              perPageOptions={[10, 25, 50, 100]}
              onPaginate={loadLibraryFiles}
            />
          </div>
        </Modal.Body>
      </Modal>
      )}
    </div>
  );
};

const parseEditorValue = (value: string) => {
  if (!value) {
    return {};
  }

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getRawEditorState = (editorValue: any) => {
  return editorValue?.EditorState ??
    (editorValue?.blocks ? editorValue : undefined);
};

const isEmptyRawEditorState = (rawEditorState: any) => {
  return (
    rawEditorState?.blocks?.length === 1 &&
    rawEditorState.blocks[0]?.text === "" &&
    Object.keys(rawEditorState?.entityMap ?? {}).length === 0
  );
};

export default function InputArgumentField({
  arg,
  values,
  onChange,
  inputArgumentsInEditor,
  useOffcanvasForContactGroupModal = false,
  projectUuid,
}: InputArgumentFieldProps) {
  const placeholder = arg.Default ? `Default: ${arg.Default}` : "Enter a value";

  return (
    <div>
      {["long-text", "short-text"].includes(arg.Format) &&
        !arg.IsSelect &&
        (arg?.DataStructure === "Duration" ? (
          <VariableDurationInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ) : (
          <VariableTextInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ))}

      {["editor"].includes(arg.Format) && (
        <>
          {(values.length > 0 ? values : [""]).map((value, index) => {
            const editorValue = parseEditorValue(value);
            const rawEditorState = getRawEditorState(editorValue);

            return (
              <div
                key={`${index}-${values.length > 0 ? "initialized" : "empty"}`}
                className="mb-1 token-editor-container"
                data-token-editor
              >
                {React.createElement(TokenEditor as any, {
                  style: {
                    height: 300,
                    maxWidth: "100%",
                    overflowX: "hidden",
                    overflowY: "auto",
                  },
                  showButtons: true,
                  initialText: "",
                  rawEditorState,
                  showShortlinksButton: false,
                  tokenTags: [
                    "contact_field",
                    "input_argument",
                    "input_argument_count",
                    "workflow_name",
                    "guid",
                    "component_name",
                    "current_time",
                    "customer_name",
                    "survey",
                  ],
                  inputArguments: inputArgumentsInEditor,
                  onChange: ({
                    rawEditorState,
                    tokens,
                  }: {
                    rawEditorState: unknown;
                    tokens: unknown;
                  }) => {
                    if (
                      (!values || values.length === 0) &&
                      isEmptyRawEditorState(rawEditorState)
                    ) {
                      return;
                    }

                    if (!values || values.length === 0) {
                      onChange([
                        JSON.stringify({
                          EditorState: rawEditorState,
                          Tokens: tokens,
                        }),
                      ]);
                      return;
                    }
                    onChange(
                      values.map((item, i) =>
                        i === index
                          ? JSON.stringify({
                              EditorState: rawEditorState,
                              Tokens: tokens,
                            })
                          : item
                      )
                    );
                  },
                })}
              </div>
            );
          })}
        </>
      )}

      {["short-text"].includes(arg.Format) &&
        arg.IsSelect &&
        (arg?.DataStructure === "" ? (
          <Input
            placeholder={placeholder}
            inputType={arg.IsSelect ? "select" : undefined}
            isMulti={arg.IsMultiple}
            options={
              arg.IsSelect
                ? arg.SelectOptions?.map((opt) => ({
                    value: opt,
                    label: opt,
                  })) || []
                : undefined
            }
            isValidNewOption={(v: string) => v.trim() !== ""}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Buttons" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMultiple={arg.IsMultiple}
            options={getButtonInputOptions(arg.SelectOptions, arg.SelectOptionsIcons)}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : null)}

      {["configuration"].includes(arg.Format) &&
        (arg?.DataStructure === "Time Zones" ? (
          <Input
            placeholder={placeholder}
            inputType="select"
            isMulti={arg.IsMultiple}
            options={[
              {
                value: "",
                label: `Project default (${(pageProps as any).defaultTZ})`,
              },
              ...timezones.map((tz: any) => ({
                value: tz.tzCode,
                label: tz.label,
              })),
            ]}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Yes / No" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMulti={arg.IsMultiple}
            options={[
              {
                value: "no",
                label: "No",
              },
              {
                value: "yes",
                label: "Yes",
              },
            ]}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Buttons" ? (
          <Input
            placeholder={placeholder}
            inputType="button"
            isMultiple={arg.IsMultiple}
            options={getButtonInputOptions(arg.SelectOptions, arg.SelectOptionsIcons)}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : arg?.DataStructure === "Duration" ? (
          <VariableDurationInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ) : (pageProps as any).configurations?.[arg?.DataStructure ?? ""] ? (
          <Input
            placeholder={placeholder}
            inputType="select"
            isMulti={arg.IsMultiple}
            options={Object.entries(
              (pageProps as any).configurations?.[arg?.DataStructure ?? ""] ||
                {}
            ).map(([value, label]) => ({ value, label }))}
            isValidNewOption={() => false}
            inputHelp=""
            value={values}
            onChange={onChange}
          />
        ) : (
          <VariableTextInput
            arg={arg}
            values={values}
            onChange={onChange}
            placeholder={placeholder}
          />
        ))}

      {["geo-json"].includes(arg.Format) && (
        <Input
          inputType="map"
          mapDrawingModes={arg.GeoJsonTypes}
          value={values}
          onChange={onChange}
          isMultiple={arg.IsMultiple}
        />
      )}

      {["json"].includes(arg.Format) && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => onChange([...(values ?? []), ""])}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <Input
                  inputType="textArea"
                  placeholder={placeholder}
                  value={[value]}
                  onChange={([v]) => {
                    const jsonString = v;
                    onChange(
                      values.map((item, i) => (i === index ? jsonString : item))
                    );
                  }}
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["business-hours"].includes(arg.Format) && (
        <Input inputType="businessHours" value={values} onChange={onChange} />
      )}

      {["email"].includes(arg.Format) && (
        <Input
          placeholder={placeholder}
          isMulti={arg.IsMultiple}
          inputHelp=""
          value={values}
          isValidNewOption={(emailString: string) => {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(emailString);
          }}
          onChange={onChange}
        />
      )}

      {["number"].includes(arg.Format) && (
        <Input
          placeholder={placeholder}
          isMulti={arg.IsMultiple}
          inputType={arg.IsSelect ? "select" : undefined}
          options={
            arg.IsSelect
              ? arg.SelectOptions?.map((opt) => ({
                  value: opt,
                  label: opt,
                })) || []
              : undefined
          }
          isValidNewOption={(v: string) => !!parseFloat(v.trim())}
          onCreateOption={(v: string) => {
            if (!!parseFloat(v.trim())) {
              onChange([...(values || []), v.trim()]);
            }
          }}
          inputHelp=""
          value={values}
          onChange={onChange}
          type="number"
          min={arg.Min}
          max={arg.Max}
        />
      )}

      {(arg.Format === "date" ||
        arg.Format === "time" ||
        arg.Format === "dateTime") && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => {
                const date = arg.Default
                  ? new Date(String(arg.Default))
                  : new Date();
                onChange([
                  ...(values ?? []),
                  formatDateValue(date, arg.Format),
                ]);
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <ReactDatePicker
                  showTimeSelect={
                    arg.Format === "time" || arg.Format === "dateTime"
                  }
                  showTimeSelectOnly={arg.Format === "time"}
                  className="form-control"
                  selected={
                    value &&
                    (arg.Format === "time"
                      ? new Date(
                          new Date().setHours(
                            ...(value.split(":").map(Number) as [
                              number,
                              number
                            ])
                          )
                        )
                      : new Date(value))
                  }
                  dateFormat={
                    (arg.Format === "dateTime" && "yyyy-MM-dd HH:mm") ||
                    (arg.Format === "date" && "yyyy-MM-dd") ||
                    (arg.Format === "time" && "HH:mm") ||
                    undefined
                  }
                  timeFormat="HH:mm"
                  onChange={(date: Date | null) => {
                    if (!date) {
                      onChange([""]);
                      return;
                    }
                    if (!values || values.length === 0) {
                      onChange([formatDateValue(date, arg.Format)]);
                      return;
                    }

                    onChange(
                      values.map((item, i) =>
                        i === index ? formatDateValue(date, arg.Format) : item
                      )
                    );
                  }}
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["sms", "voice"].includes(arg.Format) && (
        <>
          {arg.IsMultiple && (
            <button
              type="button"
              title="Add input"
              className="btn btn-outline-primary btn-sm border-0 float-end"
              onClick={() => onChange([...(values ?? []), ""])}
            >
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          )}
          {(values.length > 0 ? values : [""]).map((value, index) => (
            <div key={index} className="d-flex mb-1">
              <div className="flex-grow-1">
                <PhoneInputComponent
                  placeholder={placeholder}
                  value={[value]}
                  onChange={(newValue) =>
                    onChange(
                      values.map((item, i) =>
                        i === index ? newValue[0] ?? "" : item
                      )
                    )
                  }
                />
              </div>
              {arg.IsMultiple && values.length > 1 && (
                <button
                  type="button"
                  title="Remove input"
                  className="btn btn-outline-danger border-0 float-end"
                  onClick={() => onChange(values.filter((_, i) => i !== index))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {["asset"].includes(arg.Format) && (
        <Input
          placeholder="Select assets"
          value={values}
          onChange={onChange}
          loadOptions={loadAssetOptionsPaginated}
          getValueLabels={getAssetValueLabels}
          formatOptionLabel={formatAssetOptionLabel}
          options={undefined}
          inputType="select"
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
        />
      )}

      {["contact"].includes(arg.Format) && (
        <Input
          placeholder="Select contacts"
          value={values}
          onChange={onChange}
          inputType="contact"
          isMultiple={arg.IsMultiple}
          modalAsOffcanvas={useOffcanvasForContactGroupModal}
        />
      )}

      {["group"].includes(arg.Format) && (
        <Input
          placeholder="Select groups"
          value={values}
          onChange={onChange}
          inputType="contactSet"
          isMultiple={arg.IsMultiple}
          modalAsOffcanvas={useOffcanvasForContactGroupModal}
        />
      )}

      {["event"].includes(arg.Format) && (
        <Input
          placeholder="Select events"
          value={values}
          onChange={onChange}
          loadOptions={(search: string, prevOptions: unknown) =>
            loadEventOptionsPaginated(search, prevOptions)
          }
          inputType="select"
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
        />
      )}

      {["ticket"].includes(arg.Format) && (
        <Input
          placeholder="Enter ticket reference"
          value={values}
          onChange={onChange}
          isMulti={arg.IsMultiple}
          isClearable={!arg.IsRequiredManual}
          isValidNewOption={(v: string) => v.trim() !== ""}
          inputHelp=""
        />
      )}

      {["binary", "file"].includes(arg.Format) && (
        <BinaryFileInput arg={arg} values={values} onChange={onChange} projectUuid={projectUuid} />
      )}

      {getFieldError(arg, values ?? []) && (
        <div className="mt-1">{getFieldError(arg, values ?? [])}</div>
      )}
    </div>
  );
}
